import t from "@/lang/lang";
import {
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@/shared/components/ui/table";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { Star } from "lucide-react";
import { formatDate } from "@/shared/lib/utilities/date";
import { DocumentIcon } from "@/services/backend/documents/components/document-icon";
import {
  ScrollBodyTable,
  ScrollBodyTableHeader,
} from "@/shared/components/tables/table-scroll-body";
import { ScrollArea, ScrollBar } from "@/shared/components/ui/scroll-area";
import { useSearchContext } from "@/routes/_components/search/search-context";
import { NavLink, useNavigate } from "react-router";
import { Button } from "@/shared/components/ui/button";
import { useDocumentPath } from "@/services/backend/documents/components/use-document-path";
import { DocumentPreview } from "@/services/backend/documents/components/preview/document-preview";
import { DocumentKind } from "@bitory-gmbh/bsm";

export interface DocumentResult {
  id: string;
  documentKind: DocumentKind;
  topResult: boolean;
  documentNumber: string;
  processId: string;
  serviceKind: GSMServiceKind;
  processNumber: string;
  customerId: string;
  customerName: string;
  companyId: string;
  companyName: string;
  createdAt: string;
  updatedAt: string;
}

export function DocumentResults({
  documents,
  expanded = false,
}: {
  documents: DocumentResult[];
  expanded?: boolean;
}) {
  const enableScroll = !expanded && documents.length > 4;

  return (
    <div>
      <div className="flex justify-between">
        <h5 className="text-lg font-bold">{t("Dokumente")}</h5>
        <span className="text-muted-foreground text-sm">
          {documents.length} {t("gefunden")}
        </span>
      </div>
      <ScrollArea className={enableScroll ? "h-[20vh] border-b" : ""}>
        <ScrollBodyTable>
          <ScrollBodyTableHeader>
            <TableRow>
              <TableHead>{t("Top")}</TableHead>
              <TableHead>{t("Typ")}</TableHead>
              <TableHead>{t("Nummer")}</TableHead>
              <TableHead>{t("Kunde")}</TableHead>
              <TableHead>{t("Vorgang")}</TableHead>
              <TableHead>{t("Datum")}</TableHead>
              <TableHead>{t("Aktionen")}</TableHead>
            </TableRow>
          </ScrollBodyTableHeader>
          <TableBody>
            {documents.map((document) => (
              <DocumentResultTableRow document={document} key={document.id} />
            ))}
            {documents.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-2 text-center">
                  {t("Keine Dokumente gefunden")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </ScrollBodyTable>
        {enableScroll && <ScrollBar forceMount />}
      </ScrollArea>
    </div>
  );
}

function DocumentResultTableRow({ document }: { document: DocumentResult }) {
  const navigate = useNavigate();
  const { setOpen } = useSearchContext();

  return (
    <TableRow>
      <TableCell className="py-0 whitespace-nowrap">
        {document.topResult && <Star className="h-5 w-5" />}
      </TableCell>
      <TableCell className="py-0 whitespace-nowrap">
        <div className="flex space-x-2">
          <DocumentIcon documentKind={document.documentKind} />
          <span>{t(document.documentKind)}</span>
        </div>
      </TableCell>
      <TableCell className="py-0 whitespace-nowrap">
        {document.documentNumber}
      </TableCell>
      <TableCell className="py-0 whitespace-nowrap">
        {document.customerName}
      </TableCell>
      <TableCell className="py-0 whitespace-nowrap">
        {document.processNumber}
      </TableCell>
      <TableCell className="py-0 whitespace-nowrap">
        {formatDate(document.updatedAt)}
      </TableCell>
      <TableCell className="flex py-0 whitespace-nowrap">
        <DocumentPreview
          documentId={document.id}
          documentKind={document.documentKind}
          serviceKind={document.serviceKind}
          onNavigateToDocument={(path) => {
            setOpen(false);
            navigate(path);
          }}
        />
        <NavigateToDocument document={document} />
      </TableCell>
    </TableRow>
  );
}

function NavigateToDocument({ document }: { document: DocumentResult }) {
  const { id, serviceKind, processId, documentKind } = document;
  const { setOpen } = useSearchContext();

  const { path } = useDocumentPath({
    documentId: id,
    documentKind,
    serviceKind,
    processId,
  });

  if (!path) {
    return null;
  }

  return (
    <NavLink to={path}>
      <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
        <span>{t("Navigieren")}</span>
      </Button>
    </NavLink>
  );
}
